<script>
	import { onMount } from "svelte";

	import { Router, Route, navigate } from "svelte-routing";
	import Drawer, { AppContent, Content, Header, Title, Subtitle, Scrim } from "@smui/drawer";
	import Snackbar, { Actions, Label } from "@smui/snackbar";
	import IconButton from "@smui/icon-button";

	import GraphicsList from "./components/GraphicsList.svelte";
	import GraphicView from "./components/GraphicView.svelte";
	import GraphicInstanceView from "./components/GraphicInstanceView.svelte";
	import DialogDelete from "./components/DialogDelete.svelte";

	import api from "../../api";
	import { deepcopy, random, optionsToObject, transformStringToInputObject, flattenOptions } from "../../utils";
	import {
		loggedin,
		routesById,
		graphics,
		getInstance,
		selectedLanguage,
		selectedOrganization,
		selectedOrganizationObj,
		selectedProject,
		setupComplete,
		debug,
	} from "../../stores";
	import ProjectSettings from "./components/ProjectSettings.svelte";
	import ArticlePreview from "../ArticlePreview.svelte";

	let showProjectSettings;
	let showArticlePreview;

	let selectedGraphic;
	let selectedInstance;
	let translations = [];

	let refreshString = "";
	let modified = {};
	let instanceToDelete;

	let snackbar;
	let saveErrorSnackbar;
	let saveErrorMessage = "";

	// ///////////////////////////////////////////////////////////
	// re-routing
	// ///////////////////////////////////////////////////////////
	// if not logged in, reroute to login
	$: if (!$loggedin) {
		navigate($routesById.login.path);
	}

	// if selected Organization does not have access to the selected project, reroute to projects page
	$: if ($setupComplete && !$selectedOrganizationObj.projects.includes($selectedProject)) {
		$selectedProject = undefined;
		navigate($routesById.projects.path);
	}

	// ///////////////////////////////////////////////////////////
	// functions for getting data from backend
	// ///////////////////////////////////////////////////////////
	const initOption = (o) => {
		const allValues = flattenOptions(o.options);
		let generatedDefault;
		if (o.multi) {
			const shuffled = [...allValues].sort(() => 0.5 - Math.random());
			let count = Math.floor(shuffled.length / 2);
			if (o.min != null) count = Math.max(count, o.min);
			if (o.max != null) count = Math.min(count, o.max);
			generatedDefault = shuffled.slice(0, count);
		} else {
			generatedDefault = allValues[0];
		}
		const noValue = o.multi ? [] : null;
		return o.required ? o.default || generatedDefault : o.default || noValue;
	};

	const fetchGraphics = async (resetGraphic = false) => {
		if (!$setupComplete) {
			navigate($routesById.projects.path);
			return;
		}
		const url = `graphics/?project=${$selectedProject}&language=${$selectedLanguage}&organisation=${$selectedOrganization}&order=title`;
		$graphics = await api.get(url);
		$graphics.forEach((g) => {
			g.graphic_options.forEach((o) => {
				o.selected = o.selected || initOption(o);
				o.original = deepcopy(o.selected);
				o.modified = false;
			});
		});
		if (resetGraphic) {
			selectedGraphic = $graphics.length > 0 ? $graphics[0] : undefined;
			selectedInstance = undefined;
		}

		if (selectedInstance) selectedInstance = getInstance(selectedInstance.graphicinstance_id);

		if ($debug) console.log("GRAPHICS", $graphics);
	};

	const fetchTranslations = async (data) => {
		translations = [];
		if (!selectedInstance) {
			return;
		}
		translations = data || (await api.get(`graphicInstances/${selectedInstance.graphicinstance_id}/translations`));
		translations.forEach((t) => {
			if (t.translation) {
				t.input = t.translation;
				t.modified = false;
			} else if (t.suggestions.length > 0 && !data) {
				t.input = t.suggestions[0].string;
				t.isSuggestion = true;
        t.isMachineTranslation = t.suggestions[0].is_machine_translation;
        t.suggestions = t.suggestions.map(x => x.string);
				t.modified = true;
			} else {
				t.input = null;
				t.isMissing = true;
				t.modified = false;
			}
			t.original = t.input;
		});
	};

	// ///////////////////////////////////////////////////////////
	// enable edit mode
	// ///////////////////////////////////////////////////////////
	window.addEventListener("message", (e) => {
		if (e.data && e.data.type === "euroviz-edit") {
			const instance = getInstance(e.data.id);
			modified.heading = e.data.edit.heading !== instance.heading ? e.data.edit.heading : null;
			modified.description = e.data.edit.description !== instance.description ? e.data.edit.description : null;
			modified.byline = e.data.edit.byline !== instance.byline ? e.data.edit.byline : null;
		}
	});

	// ///////////////////////////////////////////////////////////
	// handle instances
	// ///////////////////////////////////////////////////////////
	// for instances convert the options object to an array that includes all information
	// for the input from the graphic and the currently selected options for the instance
	const createOptionsForInput = (instance, graphic) => {
		// only create the options once for every instance --> if options is already an array, they were already created
		if (!graphic || !instance || Array.isArray(instance.options)) return;
		instance.options = graphic.graphic_options.map((o) => ({
			...o,
			modified: false,
			original: deepcopy(instance.options[o.id] || initOption(o)),
			selected: deepcopy(instance.options[o.id] || initOption(o)),
		}));
	};
	$: createOptionsForInput(selectedInstance, selectedGraphic);

	const createOverwriteFontthemeForInput = (instance) => {
		if (!instance || (typeof instance.overwrite_font_theme !== "string" && instance.overwrite_font_theme !== null))
			return;
		instance.overwrite_font_theme = transformStringToInputObject(instance.overwrite_font_theme);
	};
	$: createOverwriteFontthemeForInput(selectedInstance);

	const createOverwriteColorthemeForInput = (instance) => {
		if (!instance || (typeof instance.overwrite_color_theme !== "string" && instance.overwrite_color_theme !== null))
			return;
		instance.overwrite_color_theme = transformStringToInputObject(instance.overwrite_color_theme);
	};
	$: createOverwriteColorthemeForInput(selectedInstance);

	const createInstance = async (graphic, options) => {
		const newInstance = await api.post("graphicInstances/", {
			graphic: graphic.id,
			organisation: $selectedOrganization,
			language: $selectedLanguage,
			project: $selectedProject,
			options: optionsToObject(options),
		});

		selectedInstance = newInstance;
		fetchGraphics();
		modified = {};
	};

	const deleteInstance = async (instance) => {
		await api.delete(`graphicInstances/${instance.graphicinstance_id}/`);
		if (selectedInstance.graphicinstance_id === instance.graphicinstance_id) selectedInstance = undefined;
		fetchGraphics();
		instanceToDelete = undefined;
		modified = {};
		snackbar.open();
	};

	const saveInstance = async (instance, translations, options, fonttheme, colortheme, geo) => {
		// save changes made in the graphic itself and its options
		const newData = {
			options: optionsToObject(options),
			overwrite_font_theme: fonttheme.selected,
			overwrite_color_theme: colortheme.selected,
		};

		if (modified.heading || modified.heading === "") newData.heading = modified.heading;
		if (modified.byline || modified.byline === "") newData.byline = modified.byline;
		if (modified.description || modified.description == "") newData.description = modified.description;

		let modifiedTranslations = translations.map((t) => ({ string: t.string, translation: t.input }));
		try {
			await api.patch(`graphicInstances/${instance.graphicinstance_id}/`, newData);

			// save translations
			modifiedTranslations = await api.post(
				`graphicInstances/${instance.graphicinstance_id}/translations/`,
				modifiedTranslations
			);
		} catch (e) {
			saveErrorMessage = `Saving failed: ${e.message}`;
			saveErrorSnackbar.open();
			return;
		}

		// save place-name translations (only dirty rows); a geo failure must not
		// strand the already-persisted instance/translations state above
		if (geo && geo.rows.length > 0) {
			try {
				const savedGeoRows = await api.post(`graphicInstances/${instance.graphicinstance_id}/geoTranslations/`, geo.rows);
				await geo.onSaved(savedGeoRows);
			} catch (e) {
				if (geo.onError) geo.onError(e);
			}
		}

		fetchTranslations(modifiedTranslations);
		fetchGraphics();

		modified = {};
		refreshString = random();
	};

	// ///////////////////////////////////////////////////////////
	// react to user input
	// ///////////////////////////////////////////////////////////
	// if project, language or organization changes, get the new data from backend
	$: $selectedProject, $selectedLanguage, $selectedOrganization, fetchGraphics(true);

	// if user picks a different instance, get the corresponding translations
	$: selectedInstance, selectedGraphic, fetchTranslations();
</script>

{#if $loggedin && $setupComplete}
	<div class="container">
		<GraphicsList bind:showProjectSettings bind:showArticlePreview bind:selectedGraphic bind:selectedInstance bind:instanceToDelete />
		<Scrim fixed={false} />
		{#if showArticlePreview}
			<ArticlePreview />
		{:else if showProjectSettings}
			<ProjectSettings />
		{:else if selectedInstance}
			<GraphicInstanceView
				graphic={selectedGraphic}
				instance={selectedInstance}
				{createInstance}
				{saveInstance}
				bind:translations
				bind:modified
				bind:instanceToDelete
				{refreshString}
			/>
		{:else if selectedGraphic}
			<GraphicView graphic={selectedGraphic} {createInstance} />
		{/if}
	</div>
	<DialogDelete bind:open={instanceToDelete} on:click={deleteInstance(instanceToDelete)} />
	<Snackbar bind:this={snackbar}>
		<Label>Deleted graphic.</Label>
		<Actions>
			<IconButton class="material-icons" title="Dismiss">close</IconButton>
		</Actions>
	</Snackbar>
	<Snackbar bind:this={saveErrorSnackbar}>
		<Label>{saveErrorMessage}</Label>
		<Actions>
			<IconButton class="material-icons" title="Dismiss">close</IconButton>
		</Actions>
	</Snackbar>
{/if}

<style lang="scss">
	.container {
		position: relative;
		display: flex;
		border: 1px solid var(--mdc-theme-text-hint-on-background, rgba(0, 0, 0, 0.1));
		overflow: hidden;
		z-index: 0;
		height: 100%;
	}
</style>
